import { formatDateDisplay, formatDateISO } from "../../helpers/dateUtils";
import { logDetailed } from "../../helpers/utils";

export default {
  data() {
    return {
      editedPatient: this.defaultEditedPatient(),
      isEditingPatient: false,
      isAddingPatient: false,
      isAddingExam: false,
      isSavingPatient: false,
      isManualUpload: false,
      isMergingPatient: false,
      canEditPatient: true,
      patientsToMerge: []
    };
  },
  methods: {
    handleNewPatient() {
      this.isAddingPatient = true;
      this.isAddingExam = true;
      this.canEditPatient = true;
    },
    handleEditPatient(patient) {
      // copy the patient being edited so that changes are made as a single transaction
      this.editedPatient = Object.assign({}, patient);
      this.editedPatient.dob = patient.isoDOB;
      this.isEditingPatient = true;
      this.canEditPatient = true;
    },
    handleManualUpload(patient) {
      this.editedPatient = Object.assign(this.defaultEditedPatient(), patient);
      this.isManualUpload = true;
      this.isAddingExam = true;
      this.canEditPatient = true;
    },
    closeEditPatient() {
      this.editedPatient = this.defaultEditedPatient();
      this.isAddingPatient = false;
      this.isEditingPatient = false;
      this.isAddingExam = false;
      this.isManualUpload = false;
      this.isMergingPatient = false;
      this.canEditPatient = false;
      this.patientsToMerge = [];
      logDetailed("AddEditPatient", "closeEditPatient");
      this.$store.dispatch("getPatients");
    },
    defaultEditedPatient() {
      return {
        gender: "U",
        exam: this.defaultPatientExam(),
        files: []
      };
    },
    defaultPatientExam() {
      return {
        date: formatDateISO(Date.now()),
        eye: "U"
      };
    },
    skipExam(doSkipExam) {
      this.isAddingExam = !doSkipExam;
    },
    submitEditPatient() {
      var workflowPromise = Promise.resolve();
      if (this.isEditingPatient) {
        workflowPromise = workflowPromise
          .then(() => this.$store.dispatch("editPatient", this.editedPatient))
          .then(response => {
            var data = response.data;
            if (data.success) return Promise.resolve(data.message);

            // we expect to receive a string status result and possibly an array of matching patients
            let errorMessage = data.message;
            if (data.matchFound) {
              errorMessage = "Update Complete. Patient Match found.";
              this.isMergingPatient = true;
              this.patientsToMerge = data.matchingPatients.map(patient => {
                return {
                  ptSrNo: patient.ptSrNo,
                  ptID: patient.ptID,
                  nameLast: patient.nameLast,
                  nameFirst: patient.nameFirst,
                  dob: formatDateDisplay(patient.dob)
                };
              });
            } else if (data.locked) {
              errorMessage = "Patient Locked. Can not Update.";
            } else if (data.unauthorized) {
              errorMessage = "You are not permitted to edit this patient.";
            }
            // if the update fails, don't continue with the normal close modal procedure
            return Promise.reject(errorMessage);
          });
      } else if (this.isAddingPatient) {
        workflowPromise = workflowPromise
          .then(() => this.$store.dispatch("addPatient", this.editedPatient))
          .then(response => {
            // when adding a patient, we expect to receive a new patient serial number
            this.editedPatient.ptSrNo = response.data.ptSrNo;
          });
      }

      // when adding an exam, make sure to save the exam and upload exam files
      if (this.isAddingExam) {
        this.$store.commit("setLoadingMessage", "Uploading exam files");
        workflowPromise = workflowPromise
          .then(() => this.$store.dispatch("saveExam", this.editedPatient))
          .catch(() => {
            return Promise.reject("An error occurred while uploading the exam. Please try again later.");
          })
          .then(response => {
            this.editedPatient.exam.exSrNo = response.data.exSrNo;
            return this.uploadExamFiles(this.editedPatient);
          })
          .then(response => {
            logDetailed("AddEditPatient", "submitEditPatient", "Complete file upload response", response, this.editedPatient);
            let hasErrors = this.editedPatient.files.some(f => f.state === "Error");
            if (hasErrors) {
              // when uploadinf files fails in some way, disable editing.
              this.canEditPatient = false;
              return Promise.reject("An error occurred while uploading files. Please try again later.");
            }
            return Promise.resolve(response);
          })
          .catch(response => {
            logDetailed("AddEditPatient", "submitEditPatient", "Complete file upload response", response, this.editedPatient);
            return Promise.reject(response);
          })
          .finally(() => this.$store.commit("setLoadingMessage", null));
      }

      return workflowPromise;
    },
    mergePatients() {
      const ptSrNos = [this.editedPatient.ptSrNo];
      this.patientsToMerge.forEach(p => ptSrNos.push(p.ptSrNo));
      return this.$store.dispatch("mergePatients", ptSrNos)
        .then(response => {
          var data = response.data;
          if (data.success) return Promise.resolve(data.message);

          let errorMessage = "An unexpected error occurred";
          if (data.unauthorized)
            errorMessage = "Patient Merging Error. Action not permitted";
          return Promise.reject(errorMessage);
        });
    },
    clearSavingState() {
      this.isSavingPatient = false;
    },
    addEditPatientSubmitError(response) {
      if (typeof response === "string")
        this.$store.commit("displayStatus", { message: response, level: "error" });

      // todo - if the response isn't a string, it should be an axios object
      // this object should describe a server error, and should be handled
    },
    handleSubmitEditPatient() {
      return this.cleanUpAddEditModal(this.submitEditPatient());
    },
    handleMergePatients() {
      return this.cleanUpAddEditModal(this.mergePatients());
    },
    cleanUpAddEditModal(promise) {
      this.isSavingPatient = true;
      // to clean up, refresh the patient list and selected patient exam
      return promise.then(() => this.$store.dispatch("getPatients"))
        .then(() => this.$store.dispatch("getPatientExams"))
        // only close the edit modal if the save workflow succeeds
        .then(this.closeEditPatient)
        .catch(this.addEditPatientSubmitError)
        .finally(this.clearSavingState);
    }
  }
}